/**
 * Opengater events hooks (users service, `admin: events`). CRUD list of promo
 * events. The API still calls them events; the UI calls them campaigns.
 */
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, } from '@tanstack/react-query';
import { apiOg } from './client';
const PATH = '/api/v1/admin/events';
export const eventsQueryKey = ['og', 'events'];
const OWNERS_KEY = ['og', 'event-owners'];
const LIMITS_KEY = ['og', 'event-bonus-limits'];
const PAGE = 200;
const AUDIENCE_PAGE = 50;
function eventListPath(filters) {
    const params = new URLSearchParams();
    params.set('limit', String(filters.limit ?? PAGE));
    params.set('offset', String(filters.offset ?? 0));
    params.set('status', filters.status ?? 'active');
    if (filters.owner)
        params.set('owner', filters.owner);
    if (filters.q)
        params.set('q', filters.q);
    if (filters.created_from)
        params.set('created_from', filters.created_from);
    if (filters.created_to)
        params.set('created_to', filters.created_to);
    return `${PATH}?${params.toString()}`;
}
export function useOgEvents(filters = {}) {
    return useQuery({
        queryKey: [...eventsQueryKey, filters],
        queryFn: () => apiOg('users', eventListPath(filters)),
    });
}
export function useOgAudienceEvents(query, enabled = true) {
    const q = query.trim();
    return useInfiniteQuery({
        queryKey: [...eventsQueryKey, 'audience-picker', q],
        queryFn: ({ pageParam }) => apiOg('users', eventListPath({ status: 'all', limit: AUDIENCE_PAGE, offset: pageParam, q })),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const next = last.offset + last.items.length;
            return next < last.total ? next : undefined;
        },
        enabled,
    });
}
export const eventDetailQueryKey = (id) => ['og', 'event', id];
export function useOgEventDetails(ids) {
    return useQueries({
        queries: ids.map((id) => ({
            queryKey: eventDetailQueryKey(id),
            queryFn: () => apiOg('users', `${PATH}/${encodeURIComponent(String(id))}`),
        })),
    });
}
export function useOgEventOwners(enabled = true) {
    return useQuery({
        queryKey: OWNERS_KEY,
        queryFn: () => apiOg('users', `${PATH}/owners`),
        enabled,
    });
}
export function useOgEventBonusLimits(enabled = true) {
    return useQuery({
        queryKey: LIMITS_KEY,
        queryFn: () => apiOg('users', `${PATH}/bonus-limits`),
        enabled,
    });
}
function invalidateEvents(qc) {
    return qc.invalidateQueries({ queryKey: eventsQueryKey });
}
export function useCreateOgEvent() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', PATH, { method: 'POST', body }),
        onSuccess: () => invalidateEvents(qc),
    });
}
export function useUpdateOgEvent() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('users', `${PATH}/${id}`, { method: 'PUT', body: patch }),
        onSuccess: () => invalidateEvents(qc),
    });
}
export function useDeleteOgEvent() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('users', `${PATH}/${id}`, { method: 'DELETE' }),
        onSuccess: () => invalidateEvents(qc),
    });
}
export function useTransferOgEventOwner() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, owner }) => apiOg('users', `${PATH}/${encodeURIComponent(String(id))}/owner`, {
            method: 'PUT',
            body: owner,
        }),
        onSuccess: async () => {
            await Promise.all([invalidateEvents(qc), qc.invalidateQueries({ queryKey: OWNERS_KEY })]);
        },
    });
}
